/**
 * Session state shared by every screen: the active workspace, the documents
 * this person can see (loaded once, filtered in the browser), search acronyms,
 * the approvals badge, recently viewed documents and the toast.
 *
 * The API enforces visibility; the browser only filters what it was given.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { api, type DocView, type Me, type Onboarding, type SearchTerm, type TaskDone, type TaskItem } from "./api";
import { perms, type Perms } from "./access";
import { applyAccent, applyCompanyBrands, byCoOrder } from "./brand";
import { setDisplayTimeZone } from "./format";
import { search as runSearch, type Term } from "../../../shared/search";

interface Store {
  me: Me;
  p: Perms;
  ws: string; // "ALL" or a company code
  setWs: (code: string) => void;
  /** Documents in the active workspace (company + company-wide). null while loading. */
  docs: DocView[] | null;
  allDocs: DocView[] | null;
  docsError: string | null;
  terms: SearchTerm[];
  approvalsCount: number;
  draftsCount: number;
  /** My tasks, the badge (it follows the company picked in the sidebar, like the list), and where AI is on. */
  tasks: TaskItem[] | null;
  tasksDone: TaskDone[];
  tasksCount: number;
  /** Getting started (approved mockup 2026-10-26). */
  onboarding: Onboarding | null;
  reloadOnboarding: () => Promise<void>;
  reloadTasks: () => Promise<void>;
  ai: { configured: boolean; on: (company: string, feature: string) => boolean };
  reload: () => Promise<void>;
  /** Apply a company's new name, accent or logo straight away (Settings > Companies). */
  rebrand: (c: { code: string; name: string; accent: string; logo_url?: string | null }) => void;
  reloadTerms: () => Promise<void>;
  recent: string[];
  pushRecent: (id: string) => void;
  toast: (msg: string) => void;
  /** Acronym-aware search over a list (same rules as the API). */
  search: (q: string, list: DocView[]) => { rows: { d: DocView; why: string }[]; expansions: Term[] };
}

const Ctx = createContext<Store | undefined>(undefined);
const RECENT_KEY = "docvault.recent";

export function StoreProvider({ me: rawMe, children }: { me: Me; children: ReactNode }) {
  const me = useMemo(() => {
    applyCompanyBrands(rawMe.companies); // admin-set names, accents and logos (ADR-0015)
    return { ...rawMe, companies: [...rawMe.companies].sort(byCoOrder) };
  }, [rawMe]);
  const p = useMemo(() => perms(me), [me]);
  const initialWs = () => {
    const d = me.prefs.default_tenant;
    if (me.companies.length === 1) return me.companies[0]!.code;
    return d && (d === "ALL" || p.myCodes.includes(d)) ? d : "ALL";
  };
  const [ws, setWsState] = useState<string>(initialWs);
  const [allDocs, setAllDocs] = useState<DocView[] | null>(null);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [terms, setTerms] = useState<SearchTerm[]>([]);
  const [approvalsCount, setApprovalsCount] = useState(0);
  const [tasks, setTasks] = useState<TaskItem[] | null>(null);
  const [tasksDone, setTasksDone] = useState<TaskDone[]>([]);
  const [onboarding, setOnboarding] = useState<Onboarding | null>(null);
  const [aiState, setAiState] = useState<{ configured: boolean; companies: { company: string; enabled: boolean; features: Record<string, boolean> }[] }>({ configured: false, companies: [] });
  const [recent, setRecent] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(`${RECENT_KEY}.${me.user.id}`) ?? "[]") as string[]; } catch { return []; }
  });
  const [toastMsg, setToastMsg] = useState<{ text: string; n: number } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();

  const [brandRev, setBrandRev] = useState(0);
  useEffect(() => { applyAccent(ws); }, [ws, brandRev]);
  const rebrand = useCallback((c: { code: string; name: string; accent: string; logo_url?: string | null }) => {
    applyCompanyBrands([c]);
    setBrandRev((n) => n + 1);
  }, []);
  setDisplayTimeZone(me.prefs.time_zone);

  const reload = useCallback(async () => {
    try {
      const [d, a] = await Promise.all([
        api.documents("ALL"),
        p.anyApprover ? api.approvals() : Promise.resolve({ items: [] }),
      ]);
      setAllDocs(d.documents);
      setApprovalsCount(a.items.filter((i) => i.can_approve || i.can_review).length);
      setDocsError(null);
    } catch (e) {
      setDocsError((e as Error).message);
      setAllDocs((prev) => prev ?? []);
    }
  }, [p.anyApprover]);

  const reloadTerms = useCallback(async () => {
    try { setTerms((await api.searchTerms()).terms); } catch { /* search still works without acronyms */ }
  }, []);

  const reloadTasks = useCallback(async () => {
    try { const r = await api.tasks(); setTasks(r.items); setTasksDone(r.done ?? []); } catch { /* the badge is a convenience */ }
  }, []);
  useEffect(() => { void reload(); void reloadTerms(); void reloadTasks(); }, [reload, reloadTerms, reloadTasks]);
  useEffect(() => { api.aiStatus().then(setAiState).catch(() => undefined); }, []);
  const reloadOnboarding = useCallback(async () => { try { setOnboarding(await api.onboarding()); } catch { /* optional */ } }, []);
  useEffect(() => { void reloadOnboarding(); }, [reloadOnboarding]);
  const ai = useMemo(() => ({
    configured: aiState.configured,
    on: (company: string, feature: string) => aiState.companies.some((c) => (company === "ALL" || c.company === company) && c.enabled && c.features[feature]),
  }), [aiState]);

  const inWs = useCallback((d: DocView) => d.company === "CPY" || ws === "ALL" || d.company === ws, [ws]);
  const docs = useMemo(() => (allDocs ? allDocs.filter(inWs) : null), [allDocs, inWs]);
  const tasksCount = useMemo(() => {
    const list = (tasks ?? []).filter((t) => ws === "ALL" || !t.company || t.company === ws || t.company === "CPY");
    const setups = list.filter((t) => t.kind === "admin_setup").length;
    return list.length - Math.max(0, setups - 1); // My tasks shows one setup row for several companies
  }, [tasks, ws]);
  const draftsCount = useMemo(() => (docs ?? []).filter((d) => d.pending?.by.id === me.user.id).length, [docs, me.user.id]);

  const setWs = useCallback((code: string) => setWsState(code), []);

  const pushRecent = useCallback((id: string) => {
    setRecent((r) => {
      const next = [id, ...r.filter((x) => x !== id)].slice(0, 5);
      try { localStorage.setItem(`${RECENT_KEY}.${me.user.id}`, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, [me.user.id]);

  const toast = useCallback((text: string) => {
    setToastMsg((t) => ({ text, n: (t?.n ?? 0) + 1 }));
    clearTimeout(toastTimer.current);
    // Long enough to read: about 70 ms a character, between 4 and 10 seconds.
    toastTimer.current = setTimeout(() => setToastMsg(null), Math.min(10000, Math.max(4000, text.length * 70)));
  }, []);

  const search = useCallback((q: string, list: DocView[]) => {
    const scoped = terms.filter((t) => t.company === "CPY" || ws === "ALL" || t.company === ws);
    // Former numbers (ADR-0016 renumber) stay searchable.
    const res = runSearch(q, list.map((d) => ({ ...d, full_number: [d.number, ...(d.former_numbers ?? [])].join(" ") })), scoped);
    return { rows: res.hits.map((h) => ({ d: h.item as DocView, why: h.why.join(" · ") })), expansions: res.expansions };
  }, [terms, ws]);

  const value: Store = {
    me, p, ws, setWs, docs, allDocs, docsError, terms, approvalsCount, draftsCount, tasks, tasksDone, tasksCount, reloadTasks, onboarding, reloadOnboarding, ai,
    reload, rebrand, reloadTerms, recent, pushRecent, toast, search,
  };
  return (
    <Ctx.Provider value={value}>
      {children}
      <div className={`toast ${toastMsg ? "show" : ""}`} role="status" aria-live="polite">{toastMsg?.text ?? ""}</div>
    </Ctx.Provider>
  );
}

export function useStore(): Store {
  const s = useContext(Ctx);
  if (!s) throw new Error("useStore must be used within StoreProvider");
  return s;
}
